import React from 'react'
import { erc20Abi, formatUnits } from 'viem'

interface Params {
  client: any
  tokenAddress: `0x${string}`
  holder: `0x${string}`
  decimals: number
  fallbackFormatted?: string
  pollingInterval?: number
}

export function useErc20BalanceOfWatcher({
  client,
  tokenAddress,
  holder,
  decimals,
  fallbackFormatted = '0',
  pollingInterval = 12_000,
}: Params) {
  const [value, setValue] = React.useState<bigint | null>(null)
  const [formatted, setFormatted] = React.useState<string>(fallbackFormatted)

  React.useEffect(() => {
    let unwatchTo: (() => void) | undefined
    let unwatchFrom: (() => void) | undefined

    const readBalance = async () => {
      try {
        const v = (await client.readContract({
          address: tokenAddress,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [holder],
        })) as bigint
        setValue(v)
        setFormatted(formatUnits(v, decimals))
      } catch (e) {
        // 保持兜底值，待事件触发时再刷新
        setFormatted(fallbackFormatted)
      }
    }

    // 初次拉取
    readBalance()

    // 事件触发刷新：to=holder（余额增加）
    try {
      unwatchTo = client.watchContractEvent({
        address: tokenAddress,
        abi: erc20Abi,
        eventName: 'Transfer',
        args: { to: holder },
        onLogs: () => {
          readBalance()
        },
        poll: true,
        pollingInterval,
      })

      // 事件触发刷新：from=holder（余额减少）
      unwatchFrom = client.watchContractEvent({
        address: tokenAddress,
        abi: erc20Abi,
        eventName: 'Transfer',
        args: { from: holder },
        onLogs: () => {
          readBalance()
        },
        poll: true,
        pollingInterval,
      })
    } catch (e) {
      // 忽略事件订阅错误，页面仍保持兜底值
      // console.warn('watchContractEvent failed', e)
    }

    return () => {
      unwatchTo?.()
      unwatchFrom?.()
    }
  }, [
    client,
    tokenAddress,
    holder,
    decimals,
    fallbackFormatted,
    pollingInterval,
  ])

  return { value, formatted }
}
